import Logo from "@/components/Logo";
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, MessageCircleQuestion, ClipboardList, TrendingUp,
  Check, AlertTriangle, X, Minus, Send, Eye, Loader2, Lock, ArrowRight, History,
  Dumbbell, CalendarDays, MessageSquareHeart,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { UploadZone } from "@/components/mentor/UploadZone";
import PracticeTab from "@/components/mentor/PracticeTab";
import ChallengeTab from "@/components/mentor/ChallengeTab";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mediaUrl = (p) => `${API}/mentor/media/${p}`;

async function api(path, opts = {}) {
  const r = await fetch(`${API}/mentor${path}`, { credentials: "include", ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d?.detail || "Something went wrong");
  return d;
}

function VerdictIcon({ v }) {
  if (v === "correct") return <span className="h-6 w-6 rounded-full grid place-items-center bg-[#7ee0a1]/15 text-[#7ee0a1]"><Check className="h-3.5 w-3.5" /></span>;
  if (v === "review") return <span className="h-6 w-6 rounded-full grid place-items-center bg-[#e6c67a]/15 text-[#e6c67a]"><AlertTriangle className="h-3.5 w-3.5" /></span>;
  if (v === "incorrect") return <span className="h-6 w-6 rounded-full grid place-items-center bg-[#ff7a7a]/15 text-[#ff7a7a]"><X className="h-3.5 w-3.5" /></span>;
  return <span className="h-6 w-6 rounded-full grid place-items-center bg-white/5 text-zinc-600"><Minus className="h-3.5 w-3.5" /></span>;
}

function ReviewResult({ review, onRefreshSkills }) {
  const [missed, setMissed] = useState(review.missed_image_path ? { image_path: review.missed_image_path, text: review.missed_text } : null);
  const [busy, setBusy] = useState(false);
  const reveal = async () => {
    setBusy(true);
    try {
      const d = await api(`/reviews/${review.id}/missed`, { method: "POST" });
      setMissed(d);
      onRefreshSkills?.();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div className="mt-6 space-y-5" data-testid="mentor-review-result">
      <div className="glass-card p-5 sm:p-6">
        <div className="section-label text-[10px] text-[#9fd3ff]">MBM Markup Review</div>
        <div className="mt-3 flex flex-wrap gap-4 text-[12px] text-zinc-400">
          <span><span className="text-zinc-600">Market</span> · <span className="text-zinc-200">{review.market}</span></span>
          <span><span className="text-zinc-600">Timeframe</span> · <span className="text-zinc-200">{review.timeframe}</span></span>
          <span><span className="text-zinc-600">Session</span> · <span className="text-zinc-200">{review.session}</span></span>
        </div>
        <div className="mt-5 space-y-3">
          {review.categories.map((c) => (
            <div key={c.key} className="flex items-start gap-3" data-testid={`review-cat-${c.key}`}>
              <VerdictIcon v={c.verdict} />
              <div className="flex-1">
                <div className="text-[13px] text-zinc-100 font-medium uppercase tracking-wide">{c.label}
                  <span className="ml-2 text-[10px] normal-case tracking-normal text-zinc-500">{c.verdict === "not_marked" ? "not marked" : c.verdict}</span>
                </div>
                <div className="text-[12px] text-zinc-400 font-light leading-relaxed">{c.note}</div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-5 text-sm text-zinc-300 font-light leading-relaxed border-t border-white/8 pt-4">{review.summary}</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-[#7ee0a1]/20 bg-[#7ee0a1]/5 p-3 text-[12px] text-zinc-300"><span className="text-[#7ee0a1] font-medium">You did well: </span>{review.did_well}</div>
          <div className="rounded-lg border border-[#e6c67a]/20 bg-[#e6c67a]/5 p-3 text-[12px] text-zinc-300"><span className="text-[#e6c67a] font-medium">To improve: </span>{review.improve}</div>
        </div>
      </div>

      {!missed ? (
        <button onClick={reveal} disabled={busy} className="btn-solid w-full py-3.5 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="show-me-what-i-missed-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
          {busy ? "MBM is annotating your chart…" : "Show Me What I Missed"}
        </button>
      ) : (
        <div className="glass-card p-5" data-testid="missed-comparison">
          <div className="section-label text-[10px] text-[#9fd3ff] mb-4">My Markup vs MBM Markup</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest mb-2">My Markup</div>
              <img src={mediaUrl(review.image_path)} alt="My markup" className="rounded-lg border border-white/10 w-full" />
            </div>
            <div>
              <div className="text-[10px] text-[#9fd3ff] uppercase tracking-widest mb-2">MBM Markup</div>
              <img src={mediaUrl(missed.image_path)} alt="MBM markup" className="rounded-lg border border-[#9fd3ff]/25 w-full" data-testid="mbm-annotated-image" />
            </div>
          </div>
          {missed.text && <p className="mt-4 text-[12px] text-zinc-400 font-light leading-relaxed">{missed.text}</p>}
        </div>
      )}
    </div>
  );
}

function AnalyseTab({ image, setImage, onReviewed }) {
  const [busy, setBusy] = useState(false);
  const [market, setMarket] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [review, setReview] = useState(null);
  const run = async () => {
    setBusy(true);
    setReview(null);
    try {
      const d = await api("/review", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_path: image.path, market, timeframe }),
      });
      setReview(d);
      onReviewed?.();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div>
      <UploadZone image={image} onUploaded={(img) => { setImage(img); setReview(null); }} />
      {image && !review && (
        <div className="mt-4 glass-card p-4 flex flex-wrap items-center gap-3">
          <input value={market} onChange={(e) => setMarket(e.target.value)} placeholder="Market (optional)"
            className="flex-1 min-w-[130px] bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none focus:border-[#9fd3ff]/40" data-testid="mentor-market-input" />
          <input value={timeframe} onChange={(e) => setTimeframe(e.target.value)} placeholder="Timeframe (optional)"
            className="w-36 bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none focus:border-[#9fd3ff]/40" data-testid="mentor-timeframe-input" />
          <button onClick={run} disabled={busy} className="btn-solid px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60" data-testid="review-my-markup-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {busy ? "Reviewing…" : "Review My Markup"}
          </button>
        </div>
      )}
      {busy && <div className="mt-6 py-8 text-center section-label text-[11px] text-zinc-500 animate-pulse">MBM is studying your markup…</div>}
      {review && <ReviewResult review={review} />}
    </div>
  );
}

function GuidedTab({ image, setImage }) {
  const [sess, setSess] = useState(null);
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [sess]);
  const start = async () => {
    setBusy(true);
    try {
      const d = await api("/guided/start", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_path: image.path }),
      });
      setSess(d);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const send = async () => {
    if (!answer.trim() || busy) return;
    const text = answer.trim();
    setAnswer("");
    setSess((s) => ({ ...s, messages: [...s.messages, { role: "student", text }] }));
    setBusy(true);
    try {
      const d = await api(`/guided/${sess.id}/answer`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answer: text }),
      });
      setSess((s) => ({ ...s, messages: d.messages, status: d.status }));
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div>
      <div className="glass-card p-4 mb-4 text-[12px] text-zinc-400 font-light">
        <span className="text-zinc-200 font-medium">Guided Mode — </span>
        MBM won't give you the answer. It asks questions, you read the chart, it corrects you. This is how you learn to analyse without AI.
      </div>
      <UploadZone image={image} onUploaded={(img) => { setImage(img); setSess(null); }} />
      {image && !sess && (
        <button onClick={start} disabled={busy} className="mt-4 btn-solid w-full py-3.5 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="guided-start-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircleQuestion className="h-4 w-4" />}
          {busy ? "Starting…" : "Start Guided Session"}
        </button>
      )}
      {sess && (
        <div className="mt-4 glass-card p-4" data-testid="guided-chat">
          <div className="max-h-[420px] overflow-y-auto space-y-3 pr-1">
            {sess.messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "student" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-[13px] leading-relaxed ${m.role === "student" ? "bg-[#9fd3ff]/12 text-zinc-100" : "bg-white/[0.05] text-zinc-300"}`}>
                  {m.text.replace(/SESSION COMPLETE/gi, "").trim()}
                </div>
              </div>
            ))}
            {busy && <div className="text-[11px] text-zinc-500 animate-pulse">MBM is thinking…</div>}
            <div ref={endRef} />
          </div>
          {sess.status === "complete" ? (
            <div className="mt-4 rounded-lg border border-[#7ee0a1]/25 bg-[#7ee0a1]/5 p-3 text-[12px] text-[#7ee0a1] text-center" data-testid="guided-complete">
              Session complete — great work. Upload a new chart to go again.
            </div>
          ) : (
            <div className="mt-4 flex gap-2">
              <input value={answer} onChange={(e) => setAnswer(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type your answer…" className="flex-1 bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none focus:border-[#9fd3ff]/40" data-testid="guided-answer-input" />
              <button onClick={send} disabled={busy || !answer.trim()} className="btn-solid px-4 py-2.5 disabled:opacity-50" data-testid="guided-send-btn"><Send className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const SETUP_FIELDS = [
  ["market", "Market", "e.g. Gold"], ["session", "Session", "e.g. New York"], ["timeframe", "Timeframe", "e.g. 15M"],
  ["entry", "Entry", "price / zone"], ["stop", "Stop", "price"], ["rr", "R:R", "e.g. 1:3"],
  ["tp1", "TP1", "price"], ["tp2", "TP2", "price"], ["tp3", "TP3", "price"],
];

function SetupTab({ image, setImage }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ bias: "Long", confidence: 5, why: "", invalidation: "", liquidity_target: "", news_check: "" });
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      const d = await api("/setup", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_path: image.path, ...form }),
      });
      setResult(d);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div>
      <div className="glass-card p-4 mb-4 text-[12px] text-zinc-400 font-light">
        <span className="text-zinc-200 font-medium">Setup Builder — </span>
        Mark your chart, then plan the complete trade. MBM only assesses AFTER you commit to your own analysis.
      </div>
      <UploadZone image={image} onUploaded={(img) => { setImage(img); setResult(null); }} />
      {image && !result && (
        <div className="mt-4 glass-card p-5 space-y-4" data-testid="setup-builder-form">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {SETUP_FIELDS.map(([k, label, ph]) => (
              <div key={k}>
                <div className="section-label text-[8px] text-zinc-500 mb-1">{label}</div>
                <input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph}
                  className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-[#9fd3ff]/40" data-testid={`setup-${k}-input`} />
              </div>
            ))}
          </div>
          <div>
            <div className="section-label text-[8px] text-zinc-500 mb-1.5">Bias</div>
            <div className="flex gap-2">
              {["Long", "Short", "Neutral"].map((b) => (
                <button key={b} onClick={() => set("bias", b)}
                  className={`px-4 py-2 rounded-lg text-xs border transition-colors ${form.bias === b ? "border-[#9fd3ff] text-[#9fd3ff] bg-[#9fd3ff]/10" : "border-white/12 text-zinc-400"}`} data-testid={`setup-bias-${b.toLowerCase()}`}>{b}</button>
              ))}
            </div>
          </div>
          {[["why", "Why? (your reasoning)"], ["invalidation", "What invalidates the trade?"], ["liquidity_target", "What liquidity are you targeting?"], ["news_check", "Is high-impact news approaching?"]].map(([k, label]) => (
            <div key={k}>
              <div className="section-label text-[8px] text-zinc-500 mb-1">{label}</div>
              <textarea value={form[k]} onChange={(e) => set(k, e.target.value)} rows={2}
                className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-[#9fd3ff]/40 resize-none" data-testid={`setup-${k}-input`} />
            </div>
          ))}
          <div>
            <div className="section-label text-[8px] text-zinc-500 mb-1.5">Confidence in your own analysis · {form.confidence}/10</div>
            <input type="range" min={1} max={10} value={form.confidence} onChange={(e) => set("confidence", Number(e.target.value))} className="w-full accent-[#9fd3ff]" data-testid="setup-confidence-slider" />
          </div>
          <button onClick={submit} disabled={busy} className="btn-solid w-full py-3.5 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="setup-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}
            {busy ? "MBM is assessing your setup…" : "Submit for MBM Assessment"}
          </button>
        </div>
      )}
      {result && (
        <div className="mt-5 glass-card p-5 sm:p-6" data-testid="setup-result">
          <div className="flex items-center justify-between">
            <div className="section-label text-[10px] text-[#9fd3ff]">MBM Setup Assessment</div>
            <div className="font-heading text-2xl font-light">{result.overall}<span className="text-sm text-zinc-500">/10</span></div>
          </div>
          <div className="mt-4 space-y-3">
            {result.sections.map((s) => (
              <div key={s.key} data-testid={`setup-score-${s.key}`}>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-zinc-200">{s.label}</span>
                  <span className={s.score >= 7 ? "text-[#7ee0a1]" : s.score >= 4 ? "text-[#e6c67a]" : "text-[#ff7a7a]"}>{s.score}/10</span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-white/8"><div className="h-1 rounded-full bg-[#9fd3ff]" style={{ width: `${s.score * 10}%` }} /></div>
                <div className="mt-1 text-[11px] text-zinc-500 font-light">{s.note}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-zinc-300 font-light leading-relaxed border-t border-white/8 pt-4">{result.summary}</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3 text-[12px]">
            <div className="rounded-lg border border-[#7ee0a1]/20 bg-[#7ee0a1]/5 p-3 text-zinc-300"><span className="text-[#7ee0a1] font-medium">You did well: </span>{result.did_well}</div>
            <div className="rounded-lg border border-[#e6c67a]/20 bg-[#e6c67a]/5 p-3 text-zinc-300"><span className="text-[#e6c67a] font-medium">To improve: </span>{result.improve}</div>
          </div>
          {result.next_lesson && <div className="mt-3 text-[12px] text-zinc-400">Study next: <span className="text-[#9fd3ff]">{result.next_lesson}</span></div>}
          <button onClick={() => setResult(null)} className="mt-5 btn-glass text-white px-4 py-2 text-xs">Build another setup</button>
        </div>
      )}
    </div>
  );
}

function ProgressTab() {
  const [skills, setSkills] = useState(null);
  const [history, setHistory] = useState(null);
  const [feedback, setFeedback] = useState([]);
  useEffect(() => {
    api("/skills").then(setSkills).catch(() => {});
    api("/history").then(setHistory).catch(() => {});
    api("/feedback").then(setFeedback).catch(() => {});
  }, []);
  if (!skills) return <div className="py-16 text-center section-label text-[11px] text-zinc-600 animate-pulse">Loading your skill profile…</div>;
  const rated = skills.categories.filter((c) => c.total > 0);
  return (
    <div className="space-y-6">
      <div className="glass-card p-5 sm:p-6" data-testid="skill-profile">
        <div className="section-label text-[10px] text-[#9fd3ff]">MBM Trader Skill Score</div>
        <p className="mt-2 text-[12px] text-zinc-500 font-light">Scores come only from your AI-reviewed markups and setups — never invented.</p>
        {rated.length === 0 ? (
          <div className="mt-6 text-sm text-zinc-500 font-light">No reviewed submissions yet. Analyse your first markup to start building your skill profile.</div>
        ) : (
          <div className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {rated.map((c) => (
              <div key={c.key} data-testid={`skill-${c.key}`}>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-zinc-200">{c.label}</span>
                  <span className="text-zinc-400">{c.score}% · {c.total} reviewed</span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-white/8">
                  <div className={`h-1.5 rounded-full ${c.score >= 70 ? "bg-[#7ee0a1]" : c.score >= 40 ? "bg-[#e6c67a]" : "bg-[#ff7a7a]"}`} style={{ width: `${c.score}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {feedback.length > 0 && (
        <div className="glass-card glass-card-featured p-5" data-testid="coach-feedback-list">
          <div className="section-label text-[10px] text-[#e6c67a] mb-3 inline-flex items-center gap-2">
            <MessageSquareHeart className="h-3.5 w-3.5" /> Feedback From Your Coach
          </div>
          <div className="space-y-3">
            {feedback.map((fb) => (
              <div key={fb.id} className="text-[13px] text-zinc-300 font-light">
                <span className="text-zinc-100 font-medium">{fb.coach_name}: </span>{fb.text}
                <div className="mt-1 flex flex-wrap gap-2">
                  {fb.lesson_id && (
                    <Link to={`/learn/lesson/${fb.lesson_id}`} className="inline-flex items-center gap-1 text-[11px] text-[#9fd3ff] border border-[#9fd3ff]/30 rounded-full px-2.5 py-1" data-testid={`feedback-lesson-${fb.id}`}>
                      Start lesson: {fb.lesson_title} <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                  {fb.exercise_title && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-[#e6c67a] border border-[#e6c67a]/30 rounded-full px-2.5 py-1">
                      Assigned exercise: {fb.exercise_title}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {skills.recommendations?.length > 0 && (
        <div className="glass-card glass-card-featured p-5" data-testid="skill-recommendations">
          <div className="section-label text-[10px] text-[#e6c67a]">Recommended Focus</div>
          <div className="mt-3 space-y-3">
            {skills.recommendations.map((r) => (
              <div key={r.category} className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px] text-[12px] text-zinc-300 font-light">{r.message}</div>
                {r.lesson_id ? (
                  <Link to={`/learn/lesson/${r.lesson_id}`} className="btn-solid px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5" data-testid={`start-lesson-${r.category}`}>
                    Start Lesson <ArrowRight className="h-3 w-3" />
                  </Link>
                ) : (
                  <Link to="/learn" className="btn-glass text-white px-4 py-2 text-xs">Open Trainer</Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {history && (history.reviews.length > 0 || history.setups.length > 0) && (
        <div className="glass-card p-5" data-testid="mentor-history">
          <div className="section-label text-[10px] text-zinc-400 mb-4 inline-flex items-center gap-2"><History className="h-3.5 w-3.5" /> Recent Submissions</div>
          <div className="space-y-2.5">
            {history.reviews.slice(0, 8).map((r) => (
              <div key={r.id} className="flex items-center gap-3 text-[12px]">
                <img src={mediaUrl(r.image_path)} alt="" className="h-10 w-16 object-cover rounded border border-white/10" />
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-200 truncate">Markup review · {r.market} {r.timeframe}</div>
                  <div className="text-zinc-600 text-[10px]">{new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <span className="text-[#7ee0a1]">{r.categories.filter((c) => c.verdict === "correct").length}✓</span>
                <span className="text-[#e6c67a]">{r.categories.filter((c) => c.verdict === "review").length}⚠</span>
                <span className="text-[#ff7a7a]">{r.categories.filter((c) => c.verdict === "incorrect").length}✕</span>
              </div>
            ))}
            {history.setups.slice(0, 5).map((s) => (
              <div key={s.id} className="flex items-center gap-3 text-[12px]">
                <img src={mediaUrl(s.image_path)} alt="" className="h-10 w-16 object-cover rounded border border-white/10" />
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-200 truncate">Setup plan · {s.form?.market || "chart"} · {s.form?.bias}</div>
                  <div className="text-zinc-600 text-[10px]">{new Date(s.created_at).toLocaleDateString()}</div>
                </div>
                <span className="text-zinc-300">{s.overall}/10</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { id: "analyse", label: "Analyse My Markup", icon: Sparkles },
  { id: "guided", label: "Guided Mode", icon: MessageCircleQuestion },
  { id: "setup", label: "Setup Builder", icon: ClipboardList },
  { id: "practice", label: "Practice", icon: Dumbbell },
  { id: "challenge", label: "Daily Challenge", icon: CalendarDays },
  { id: "progress", label: "My Progress", icon: TrendingUp },
];

export default function ChartMentor() {
  const { entitlements } = useAuth();
  const [tab, setTab] = useState("analyse");
  const [image, setImage] = useState(null);
  const locked = entitlements && !entitlements.is_staff && entitlements.membership === "guest";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
      <div className="grain" />
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/8">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" aria-label="MBM home"><Logo className="h-5 w-auto" /></Link>
            <span className="text-zinc-700">/</span>
            <span className="section-label text-[10px] text-[#9fd3ff]">Chart Mentor</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/learn" className="text-zinc-400 hover:text-white transition-colors">Trainer</Link>
            <Link to="/journal" className="text-zinc-400 hover:text-white transition-colors">Journal</Link>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-5 lg:px-8 py-8" data-testid="chart-mentor-page">
        <div className="section-label text-[10px] text-[#9fd3ff]">MBM Chart Mentor</div>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-light tracking-tight">Learn to read the chart yourself.</h1>
        <p className="mt-3 text-sm text-zinc-400 font-light max-w-2xl">
          Mark up your chart, screenshot it, and let MBM review your work. Learn → mark up → screenshot → AI review → correct → plan → improve.
        </p>

        {entitlements === null ? (
          <div className="py-16 text-center section-label text-[11px] text-zinc-600 animate-pulse">Checking access…</div>
        ) : locked ? (
          <div className="mt-10 glass-card glass-card-featured p-10 text-center max-w-xl mx-auto" data-testid="mentor-upgrade-wall">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-white/12 mb-5"><Lock className="h-5 w-5 text-[#9fd3ff]" /></span>
            <h2 className="font-heading text-2xl font-light">Unlock Chart Mentor</h2>
            <p className="mt-3 text-sm text-zinc-400 font-light">Chart Mentor is included with every paid MBM membership — AI markup reviews, guided chart-reading sessions and your personal skill profile.</p>
            <Link to="/#pricing" className="mt-6 inline-block btn-solid px-6 py-3 text-sm font-medium" data-testid="mentor-view-plans">View memberships</Link>
          </div>
        ) : (
          <>
            <div className="mt-7 flex gap-2 overflow-x-auto pb-1">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => setTab(id)}
                  className={`whitespace-nowrap inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs border transition-colors ${tab === id ? "border-[#9fd3ff] text-[#9fd3ff] bg-[#9fd3ff]/10" : "border-white/12 text-zinc-400 hover:text-white"}`}
                  data-testid={`mentor-tab-${id}`}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
            <div className="mt-6">
              {tab === "analyse" && <AnalyseTab image={image} setImage={setImage} />}
              {tab === "guided" && <GuidedTab image={image} setImage={setImage} />}
              {tab === "setup" && <SetupTab image={image} setImage={setImage} />}
              {tab === "practice" && <PracticeTab />}
              {tab === "challenge" && <ChallengeTab />}
              {tab === "progress" && <ProgressTab />}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
